import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/common';
import TabContainer, { TabItem } from '@/components/common/TabContainer';
import { CuttingConfigLinesTab } from './CuttingConfigLinesTab';
import {
  CuttingConfig,
  CuttingConfigLine,
  CreateCuttingConfigRequest,
  UpdateCuttingConfigRequest,
} from '@/types/cutting';

interface CuttingConfigFormProps {
  cuttingConfig?: CuttingConfig;
  existingConfigLines?: CuttingConfigLine[];
  onSubmit: (data: CreateCuttingConfigRequest | UpdateCuttingConfigRequest, configLines?: CuttingConfigLine[]) => void;
  onCancel: () => void;
  onSaveAndClose?: (data: CreateCuttingConfigRequest | UpdateCuttingConfigRequest, configLines?: CuttingConfigLine[]) => void;
  loading?: boolean;
}

// Stable empty array to prevent unnecessary re-renders
const EMPTY_CONFIG_LINES: CuttingConfigLine[] = [];

export const CuttingConfigForm: React.FC<CuttingConfigFormProps> = ({
  cuttingConfig,
  existingConfigLines = EMPTY_CONFIG_LINES,
  onSubmit,
  onCancel,
  onSaveAndClose,
  loading = false,
}) => {
  const { t } = useTranslation();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<CreateCuttingConfigRequest>({
    name: '',
    note: '',
  });

  const [configLines, setConfigLines] = useState<CuttingConfigLine[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('basic');

  // Initialize form data when editing
  useEffect(() => {
    if (cuttingConfig) {
      setFormData({
        name: cuttingConfig.name,
        note: cuttingConfig.note,
      });
    } else {
      setFormData({
        name: '',
        note: '',
      });
    }
  }, [cuttingConfig]);

  // Focus on name input when modal opens
  useEffect(() => {
    const timer = setTimeout(() => {
      nameInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Memoize existingConfigLines to prevent unnecessary re-renders
  const memoizedExistingConfigLines = useMemo(() => existingConfigLines, [existingConfigLines]);

  // Load existing config lines when editing
  useEffect(() => {
    if (memoizedExistingConfigLines.length > 0) {
      setConfigLines(memoizedExistingConfigLines);
    } else {
      setConfigLines([]);
    }
  }, [memoizedExistingConfigLines]);

  const handleInputChange = useCallback((field: keyof CreateCuttingConfigRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[field]) {
        return {
          ...prev,
          [field]: '',
        };
      }
      return prev;
    });
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('cuttingConfigs.validation.nameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Filter config lines to only include those with selected cutting sizes
      const validConfigLines = configLines.filter(line => line.cuttingSizeId);

      if (cuttingConfig) {
        onSubmit({ id: cuttingConfig.id, ...formData } as UpdateCuttingConfigRequest, validConfigLines);
      } else {
        onSubmit(formData, validConfigLines);
      }
    }
  };

  const handleSaveAndClose = () => {
    if (validateForm()) {
      // Filter config lines to only include those with selected cutting sizes
      const validConfigLines = configLines.filter(line => line.cuttingSizeId);

      if (cuttingConfig) {
        onSaveAndClose?.({ id: cuttingConfig.id, ...formData } as UpdateCuttingConfigRequest, validConfigLines);
      } else {
        onSaveAndClose?.(formData, validConfigLines);
      }
    }
  };

  // Memoize the config lines change handler to prevent unnecessary re-renders
  const handleConfigLinesChange = useCallback((newConfigLines: CuttingConfigLine[]) => {
    setConfigLines(newConfigLines);
  }, []);

  // Memoize tab definitions to prevent unnecessary re-renders
  const tabs: TabItem[] = useMemo(() => [
    {
      id: 'basic',
      label: t('cuttingConfigs.tabs.basic'),
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                {t('cuttingConfigs.fields.name')} <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder={t('cuttingConfigs.placeholders.name')}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
                {t('cuttingConfigs.fields.note')}
              </label>
              <textarea
                id="note"
                rows={3}
                value={formData.note || ''}
                onChange={(e) => handleInputChange('note', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none ${
                  errors.note ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder={t('cuttingConfigs.placeholders.note')}
              />
              {errors.note && <p className="text-red-500 text-xs mt-1">{errors.note}</p>}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'configLines',
      label: t('cuttingConfigs.tabs.configLines'),
      content: (
        <CuttingConfigLinesTab
          configLines={configLines}
          onConfigLinesChange={handleConfigLinesChange}
          cuttingConfigId={cuttingConfig?.code}
        />
      ),
    },
  ], [
    t,
    formData.name,
    formData.note,
    errors.name,
    errors.note,
    configLines,
    handleConfigLinesChange,
    cuttingConfig?.code,
  ]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Tab Container */}
      <TabContainer
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          {t('cuttingConfigs.actions.cancel')}
        </Button>
        {onSaveAndClose && (
          <Button
            type="button"
            variant="secondary"
            onClick={handleSaveAndClose}
            disabled={loading}
          >
            {t('cuttingConfigs.actions.saveAndClose')}
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={loading}
        >
          {cuttingConfig ? t('cuttingConfigs.actions.update') : t('cuttingConfigs.actions.create')}
        </Button>
      </div>
    </form>
  );
};
